import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, Copy, Check } from 'lucide-react';
import { pitchesAPI } from '../services/api.js';

// PITCH PICKER — which pitch this campaign runs.
//
// WHY THIS EXISTS. A pitch is four fields (body, behaviorBlock, dtmfRules,
// openingLine) and they were edited one at a time. `Carriers — Houston` ran a new
// process-first BODY while behaviorBlock still held the previous SMB pitch's
// "engage whoever answers, do NOT chase a decision-maker" — and behaviorBlock is
// injected after the body under a header reading "THIS SECTION WINS". So the model
// correctly obeyed the old instruction while the new pitch was built entirely on
// asking for the driver desk. Four fields kept in sync by discipline is not a
// system; selecting a bundle is.
//
// ⛔ TWO THINGS THIS SCREEN MUST NOT IMPLY
//
// 1. THE SWAP IS NOT A CLEAN SWAP unless the pitch defines all four fields.
//    `defines` says which it actually supplies; the rest fall back to the
//    campaign's own columns. A picker that stays silent about that reintroduces the
//    exact bleed it was built to stop, just less visibly. So we name the gaps.
//
// 2. ⚠️ AS OF 2026-08-02 THE PANEL STILL READS THE CAMPAIGN COLUMNS DIRECTLY.
//    Core resolves the bundle (pitchResolver / the campaign_effective_pitch view),
//    but until Golf points the panel at it, selecting a pitch changes the RECORD and
//    not the CALL. Saying "pitch applied" today would be a control that reports
//    success and changes nothing — the failure mode this project keeps hitting. The
//    banner below states it plainly and comes out when Golf lands the wiring.
const PANEL_WIRED = false;

const FIELD_LABEL = {
  body: 'the pitch body',
  behaviorBlock: 'the behaviour block',
  dtmfRules: 'the phone-menu rules',
  openingLine: 'the opening line'
};
const ALL_FIELDS = ['body', 'behaviorBlock', 'dtmfRules', 'openingLine'];

export default function PitchPicker({ campaignId, campaignPitchId, campaignStatus, onChanged }) {
  const [pitches, setPitches] = useState([]);
  const [current, setCurrent] = useState(campaignPitchId || '');
  // ⛔ NOT a 404 gate. This API authenticates BEFORE it routes, so an unknown path
  // and a real one both return 401 unauthenticated, and an authenticated request to
  // a live route returns 200 — /pitches NEVER 404s. The original `if (err.status
  // === 404)` guard could therefore never fire, and I had told Alpha the surfaces
  // would "light up on their own once core deploys". They had already deployed.
  // (Charlie, 2026-08-03, correcting me — and retracting his own earlier 401 probe,
  // which gives a false positive on every endpoint for the same reason.)
  //
  // What actually decides visibility: the request failed, or this tenant genuinely
  // has no pitches. 12 of 13 tenants have none, and for them `200 {data:[]}` is the
  // right answer and staying hidden is the right behaviour.
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => { setCurrent(campaignPitchId || ''); }, [campaignPitchId]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await pitchesAPI.list();
      setPitches((r?.data || r || []).filter((p) => !p.archived));
      setFailed(false);
    } catch (err) {
      // Any failure hides the card rather than showing a broken control. A 401 here
      // means the session is gone and the app is about to bounce to login anyway.
      setFailed(true);
      if (err.status !== 401 && err.status !== 403) {
        setError(err.body?.message || err.message || 'Could not load pitches.');
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide while loading, on failure, and when this tenant has no pitches AND this
  // campaign isn't on one. A campaign already pointing at a pitch always shows the
  // control, so a selection can never become invisible and unchangeable.
  if (loading || failed) return null;
  if (!pitches.length && !campaignPitchId) return null;

  const selected = pitches.find((p) => p.id === current) || null;
  const missing = selected ? ALL_FIELDS.filter((f) => !(selected.defines || []).includes(f)) : [];

  const apply = async (pitchId) => {
    setSaving(true);
    setError('');
    setResult(null);
    try {
      const r = await pitchesAPI.selectForCampaign(campaignId, pitchId || null);
      const out = r?.data || r;
      setCurrent(out?.pitchId || '');
      setResult(out);
      onChanged?.(out);
    } catch (err) {
      // Each of these is a different fix, so don't collapse them into "failed".
      setError(
        err.status === 409 && err.body?.error === 'pitch_archived'
          ? 'That pitch is archived. Archived pitches are retired experiments and can’t govern live calls — un-archive it first.'
          : err.status === 404 && err.body?.error === 'pitch_not_found'
            ? 'That pitch no longer exists.'
            : err.status === 403
              ? 'Only an account admin can change the pitch.'
              : (err.body?.message || err.message || 'Could not change the pitch.')
      );
      setCurrent(campaignPitchId || '');
    } finally {
      setSaving(false);
    }
  };

  const duplicateCurrent = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const r = await pitchesAPI.clone(selected.id, { name: `${selected.name} (copy)` });
      const copy = (r?.data || r);
      await load();
      await apply(copy.id);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not duplicate that pitch.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card mb-6">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Pitch</h2>
        </div>
        <Link to="/pitches" className="text-xs text-stone-500 underline hover:text-stone-300">
          Manage pitches
        </Link>
      </header>

      <p className="mb-4 text-sm text-stone-400">
        A pitch is the whole script bundle — body, behaviour block, phone-menu rules and opening
        line — so switching swaps all four together instead of leaving one behind.
      </p>

      {/* ⚠️ The honest caveat. Remove with PANEL_WIRED when Golf points the panel at
          the resolver / campaign_effective_pitch view. */}
      {!PANEL_WIRED && (
        <p className="mb-4 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Selecting a pitch is <span className="text-stone-100">recorded but not yet spoken</span>.
            The call agent still reads this campaign’s own fields, so the choice below won’t change
            what Alfred says until that wiring lands. Edit the campaign fields directly for now.
          </span>
        </p>
      )}

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="block flex-1 min-w-[260px]">
          <span className="field-label">Pitch for this campaign</span>
          <select
            className="field-input"
            value={current}
            disabled={saving}
            onChange={(e) => apply(e.target.value)}
          >
            {/* null = "use this campaign's own fields", which is every campaign today. */}
            <option value="">This campaign’s own fields (no shared pitch)</option>
            {pitches.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.usedBy?.length ? ` · on ${p.usedBy.length} campaign${p.usedBy.length === 1 ? '' : 's'}` : ''}
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <button type="button" className="btn-ghost" onClick={duplicateCurrent} disabled={saving}>
            <Copy size={14} /> Duplicate for an A/B variant
          </button>
        )}
      </div>

      {/* WHAT THE PITCH DOESN'T DEFINE — the anti-bleed disclosure. */}
      {selected && (
        <div className="mt-3 text-xs">
          <span className="text-stone-500">Version </span>
          <code className="text-alfred-gold/80">{selected.version}</code>
          {missing.length === 0 ? (
            <p className="mt-1 flex items-start gap-1.5 text-emerald-300">
              <Check size={13} className="mt-0.5 shrink-0" />
              Defines all four fields — nothing falls back to the campaign.
            </p>
          ) : (
            <p className="mt-1 flex items-start gap-1.5 text-amber-200">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              This pitch doesn’t set {missing.map((f) => FIELD_LABEL[f]).join(', ')} — for{' '}
              {missing.length === 1 ? 'that' : 'those'} the campaign keeps using its own value, so a
              leftover from a previous pitch can still reach the call.
            </p>
          )}
          {/* Editing a pitch that is live on other campaigns splits an A/B test. */}
          {(selected.usedBy || []).filter((c) => c.id !== campaignId && c.status === 'active').length > 0 && (
            <p className="mt-1 text-stone-500">
              Also live on{' '}
              {selected.usedBy.filter((c) => c.id !== campaignId && c.status === 'active').map((c) => c.name).join(', ')}
              {' '}— edit it in the library and both change. Duplicate instead to test a variant.
            </p>
          )}
        </div>
      )}

      {result && (
        <p className="mt-3 text-sm text-stone-400">
          {result.pitchName
            ? <>Now set to <span className="text-stone-200">{result.pitchName}</span>.</>
            : <>Back to this campaign’s own fields.</>}
          {result.liveNow && (
            <span className="text-stone-500">
              {' '}This campaign is active — it would take effect on the next call placed, not on calls already running.
            </span>
          )}
        </p>
      )}
    </section>
  );
}
